/**
 * Client-side Stripe utilities
 *
 * Note: This file is for client-side usage only.
 * For server-side operations, use lib/stripe.ts
 */

/**
 * Create a checkout session and redirect to Stripe Checkout
 */
export async function createCheckoutSession(priceId: string): Promise<void> {
  try {
    const response = await fetch('/api/stripe/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ priceId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create checkout session');
    }

    const { url } = await response.json();
    if (url) {
      window.location.href = url;
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Create a one-time credit top-up checkout session (1 € / 100 credits,
 * quantity adjustable on the Stripe page) and redirect to Stripe Checkout.
 */
export async function createTopupCheckoutSession(
  /** Euros of credit to buy — one unit is 1 € / 100 credits. The server
   *  clamps it, so a tampered value can't mint a cheaper session. */
  quantity?: number,
  /** Path the Stripe page's back link returns to; defaults to /plans. */
  returnPath?: string,
): Promise<void> {
  const response = await fetch('/api/stripe/create-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ topup: true, quantity, returnPath }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create checkout session');
  }

  const { url } = await response.json();
  if (url) {
    window.location.href = url;
  }
}

/**
 * Open Stripe Customer Portal
 */
export async function openCustomerPortal(returnUrl?: string): Promise<void> {
  try {
    const response = await fetch('/api/stripe/create-portal-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        returnUrl:
          returnUrl ||
          (typeof window !== 'undefined' ? window.location.origin : '') +
            '/account/billing',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create portal session');
    }

    const { url } = await response.json();
    if (url) {
      window.location.href = url;
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Get current user's subscription
 */
export async function getSubscription() {
  try {
    const response = await fetch('/api/stripe/subscription');

    if (!response.ok) {
      throw new Error('Failed to fetch subscription');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
}
